/**
 * TeamsPage — Sprint 5.5 (Equipe Técnica + Ordens de Serviço).
 *
 * Aba "Equipes" no NOC. Operação de campo:
 *  - Cards: Equipes · OS abertas · OS em andamento · Instalações
 *    concluídas.
 *  - Tabela de Equipes: Nome · Líder · Qtd técnicos · Instalações.
 *  - Tabela de OS: Cliente · QR · Equipe · Instalador · Status · Data.
 *  - Modais: Nova equipe, Editar equipe, Nova OS.
 *
 * Polling de 15s.
 */
import { useCallback, useEffect, useState } from "react";
import NavIcon from "../nav/NavIcon.jsx";
import {
  createTeam,
  createWorkOrder,
  listTeams,
  listWorkOrders,
  updateTeam,
} from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";

const POLL_MS = 15000;

const WO_STATUS_STYLE = {
  open: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  scheduled: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  in_progress: "text-noc-purple bg-noc-purple/10 border-noc-purple/30",
  installed: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
  cancelled: "text-noc-muted bg-noc-bg/40 border-noc-border",
};

function WoStatusPill({ status }) {
  const cls = WO_STATUS_STYLE[status] || "text-noc-muted bg-noc-bg/40";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
      {t("teams.wo_status_" + status) || status}
    </span>
  );
}

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [woData, setWoData] = useState({
    work_orders: [], total: 0, counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // 'newTeam'|'editTeam'|'newWo'
  const [editTeam, setEditTeam] = useState(null);

  const load = useCallback((signal) => {
    Promise.all([
      listTeams({ signal }),
      listWorkOrders({}, { signal }),
    ])
      .then(([tRes, woRes]) => {
        setTeams(tRes.teams || []);
        setWoData(woRes);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  const counts = woData.counts || {};
  const teamNameById = Object.fromEntries(
    teams.map((tm) => [tm.id, tm.name])
  );

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label mb-2">Operação de campo</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">
            {t("teams.title")}
          </h1>
          <p className="text-sm text-noc-muted mt-1">
            Organize equipes técnicas e acompanhe as ordens de serviço.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            className="btn-primary h-10 px-4 inline-flex items-center gap-2"
            onClick={() => setModal("newTeam")}
          >
            <NavIcon name="users" size={15} />
            {t("teams.btnNewTeam")}
          </button>
          <button
            className="h-10 px-4 rounded-xl border border-noc-warn/30
                       hover:bg-noc-warn/10 text-noc-warn text-sm"
            onClick={() => setModal("newWo")}
          >
            {t("teams.btnNewWo")}
          </button>
        </div>
      </header>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <DashCard label={t("teams.cardTeams")}
                  value={teams.length} tone="cyan" />
        <DashCard label={t("teams.cardWoOpen")}
                  value={(counts.open || 0) + (counts.scheduled || 0)}
                  tone="sky" />
        <DashCard label={t("teams.cardWoProgress")}
                  value={counts.in_progress || 0} tone="purple" />
        <DashCard label={t("teams.cardWoInstalled")}
                  value={counts.installed || 0} tone="emerald" />
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Tabela de Equipes */}
      <div>
        <h2 className="text-sm font-semibold text-noc-text mb-1">
          {t("teams.tableTeams")}
        </h2>
        <div className="glass-card !p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[11px] uppercase tracking-[0.12em] text-noc-muted bg-noc-bg/30 border-b border-noc-divider">
              <tr>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colName")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colLeader")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colTechs")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colInstalls")}
                </th>
                <th className="text-left font-medium py-1.5 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="text-sm text-noc-muted/70
                  italic py-3 px-2">{t("common.loading")}</td></tr>
              )}
              {!loading && teams.length === 0 && (
                <tr><td colSpan={5} className="text-sm text-noc-muted/60
                  italic py-3 px-2">{t("teams.emptyTeams")}</td></tr>
              )}
              {teams.map((tm) => (
                <tr key={tm.id}
                    className="border-b border-noc-divider/70 last:border-0
                               hover:bg-noc-panel2/35 transition-colors">
                  <td className="py-1.5 px-2 text-noc-text font-medium">
                    {tm.name}
                  </td>
                  <td className="py-1.5 px-2">{tm.leader || "—"}</td>
                  <td className="py-1.5 px-2">{tm.technician_count}</td>
                  <td className="py-1.5 px-2 text-noc-ok">
                    {tm.installs_count}
                  </td>
                  <td className="py-1.5 px-2">
                    <button
                      className="btn-ghost h-8 px-3 text-xs"
                      onClick={() => {
                        setEditTeam(tm); setModal("editTeam");
                      }}
                    >
                      {t("teams.btnEdit")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tabela de OS */}
      <div>
        <h2 className="text-sm font-semibold text-noc-text mb-1">
          {t("teams.tableWo")}
        </h2>
        <div className="glass-card !p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[11px] uppercase tracking-[0.12em] text-noc-muted bg-noc-bg/30 border-b border-noc-divider">
              <tr>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colCustomer")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">QR</th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colTeam")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colInstaller")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colStatus")}
                </th>
                <th className="text-left font-medium py-1.5 px-2">
                  {t("teams.colDate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-sm text-noc-muted/70
                  italic py-3 px-2">{t("common.loading")}</td></tr>
              )}
              {!loading && woData.work_orders.length === 0 && (
                <tr><td colSpan={6} className="text-sm text-noc-muted/60
                  italic py-3 px-2">{t("teams.emptyWo")}</td></tr>
              )}
              {woData.work_orders.map((wo) => (
                <tr key={wo.id}
                    className="border-b border-noc-divider/70 last:border-0
                               hover:bg-noc-panel2/35 transition-colors">
                  <td className="py-1.5 px-2">
                    {wo.customer_id ? `#${wo.customer_id}` : "—"}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-noc-accent">
                    {wo.qr_serial || "—"}
                  </td>
                  <td className="py-1.5 px-2">
                    {wo.team_name
                      || teamNameById[wo.team_id]
                      || "—"}
                  </td>
                  <td className="py-1.5 px-2">{wo.installer || "—"}</td>
                  <td className="py-1.5 px-2">
                    <WoStatusPill status={wo.status} />
                  </td>
                  <td className="py-1.5 px-2 text-noc-muted">
                    {wo.scheduled_at
                      ? formatRelative(wo.scheduled_at)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modais */}
      {modal === "newTeam" && (
        <FormModal
          title={t("teams.btnNewTeam")}
          fields={["name", "leader", "phone", "notes"]}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await createTeam(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "editTeam" && editTeam && (
        <FormModal
          title={t("teams.btnEdit")}
          fields={["name", "leader", "phone", "notes"]}
          initial={editTeam}
          onClose={() => { setModal(null); setEditTeam(null); }}
          onSubmit={async (body) => {
            await updateTeam(editTeam.id, body);
            setModal(null);
            setEditTeam(null);
            reload();
          }}
        />
      )}
      {modal === "newWo" && (
        <FormModal
          title={t("teams.btnNewWo")}
          fields={[
            "customer_id", "contract_id", "qr_serial",
            "installer", "scheduled_at", "location", "notes",
          ]}
          teamSelect={teams}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await createWorkOrder(body);
            setModal(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    cyan: "border-noc-accent/30 text-noc-accent",
    sky: "border-noc-accent/30 text-noc-accent",
    purple: "border-noc-purple/30 text-noc-purple",
    emerald: "border-noc-ok/30 text-noc-ok",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`stat-card ${toneCls}`}>
      <div className="text-2xl font-semibold mt-2">{value}</div>
      <div className="section-label">{label}</div>
    </div>
  );
}

/**
 * Modal de formulário genérico. teamSelect (opcional) adiciona um
 * <select> de equipe — usado na criação de OS. Sem <form>; submit
 * por botão.
 */
function FormModal({ title, fields, initial, teamSelect, onClose, onSubmit }) {
  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of fields) v[f] = (initial && initial[f]) || "";
    if (teamSelect) v.team_id = (initial && initial.team_id) || "";
    return v;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k, val) =>
    setValues((prev) => ({ ...prev, [k]: val }));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body = {};
      for (const [k, v] of Object.entries(values)) {
        if (v !== "" && v !== null && v !== undefined) body[k] = v;
      }
      await onSubmit(body);
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">{title}</h3>
        <div className="space-y-2">
          {teamSelect && (
            <div>
              <label className="text-[11px] text-noc-muted block mb-0.5">
                {t("teams.fieldTeam")}
              </label>
              <select
                value={values.team_id || ""}
                onChange={(e) => setField("team_id", e.target.value)}
                className="w-full text-xs bg-noc-bg/60 border
                           border-noc-border rounded px-2 py-1
                           text-noc-text"
              >
                <option value="">— sem equipe —</option>
                {teamSelect.map((tm) => (
                  <option key={tm.id} value={tm.id}>{tm.name}</option>
                ))}
              </select>
            </div>
          )}
          {fields.map((f) => (
            <div key={f}>
              <label className="text-[11px] text-noc-muted block mb-0.5">
                {t("teams.field_" + f) || f}
              </label>
              <input
                type={f === "scheduled_at" ? "datetime-local" : "text"}
                value={values[f]}
                onChange={(e) => setField(f, e.target.value)}
                className="w-full text-xs bg-noc-bg/60 border
                           border-noc-border rounded px-2 py-1
                           text-noc-text"
              />
            </div>
          ))}
        </div>
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted
                       hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-accent/30 text-noc-accent
                       bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
